import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { models as modelsApi, projects, type ProjectCreate } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { ModelPicker } from "../shared/ModelPicker";
import { Button } from "../shared/Button";
import { cn } from "../../lib/cn";
import { decodeModel, encodeModel } from "../../lib/modelValue";
import { TASK_TYPES, type ModelChoice, type Project } from "../../lib/types";

interface Props {
  onClose: () => void;
  project?: Project; // present = edit mode
}

const empty: ProjectCreate = {
  name: "",
  description: "",
  host: "",
  ssh_user: "root",
  ssh_key_path: "",
  ssh_key_passphrase: "",
  repo_path: "",
  cli_binary: "claude",
  cli_flags:
    "-p --permission-mode acceptEdits " +
    '--allowedTools "Bash Edit Write Read Glob Grep MultiEdit WebFetch WebSearch" ' +
    "--output-format stream-json --verbose",
  fallback_models: [],
};

const FIELDS: Array<{
  key: keyof ProjectCreate;
  label: string;
  placeholder: string;
  required?: boolean;
}> = [
  { key: "name", label: "Name", placeholder: "my-project", required: true },
  { key: "description", label: "Description", placeholder: "optional one-liner" },
  { key: "host", label: "Host", placeholder: "1.2.3.4 or hostname", required: true },
  { key: "ssh_user", label: "SSH user", placeholder: "root" },
  { key: "ssh_key_path", label: "SSH key path", placeholder: "id_ed25519 or /absolute/path" },
  { key: "ssh_key_passphrase", label: "Passphrase", placeholder: "leave blank to keep current" },
  { key: "repo_path", label: "Repo path", placeholder: "/opt/myproject" },
  { key: "cli_binary", label: "CLI binary", placeholder: "claude" },
  { key: "cli_flags", label: "CLI flags", placeholder: "-p --permission-mode acceptEdits" },
  { key: "test_command", label: "Test command", placeholder: "how agents run the tests they touch, e.g. pytest <paths>" },
  { key: "agent_user", label: "Agent user", placeholder: "run agents as this user, not root (set up with scripts/agent-user-setup.sh)" },
];

function projectToForm(p: Project): ProjectCreate {
  return {
    name: p.name,
    description: p.description ?? "",
    host: p.host,
    ssh_user: p.ssh_user,
    ssh_key_path: p.ssh_key_path,
    ssh_key_passphrase: "", // never round-trip the encrypted secret
    repo_path: p.repo_path,
    cli_binary: p.cli_binary,
    cli_flags: p.cli_flags,
    default_autonomy_level: p.default_autonomy_level,
    default_model: p.default_model,
    fallback_models: p.fallback_models ?? [],
    use_worktrees: p.use_worktrees,
    secret_scan_enabled: p.secret_scan_enabled,
    manager_loop_enabled: p.manager_loop_enabled,
    worker_loop_enabled: p.worker_loop_enabled,
    max_concurrent_auto: p.max_concurrent_auto,
    auto_merge: p.auto_merge,
    allowed_automerge_types: p.allowed_automerge_types ?? [],
    triage_requires_confirmation: p.triage_requires_confirmation,
    quota_pause_threshold: p.quota_pause_threshold,
    auto_token_window_budget: p.auto_token_window_budget,
    risk_autonomy_policy: p.risk_autonomy_policy ?? {},
    test_command: p.test_command ?? "",
    agent_user: p.agent_user ?? "",
  };
}

export function ProjectForm({ onClose, project }: Props) {
  const isEdit = !!project;
  const [form, setForm] = useState<ProjectCreate>(
    project ? projectToForm(project) : empty,
  );
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const qc = useQueryClient();

  const set = <K extends keyof ProjectCreate>(k: K, v: ProjectCreate[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const modelCatalog = useQuery({
    queryKey: ["models"],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const allModels = modelCatalog.data?.models ?? [];
  const fallbackOptions = allModels;
  // The (cli, model) pairs already in the chain — used by the add dropdown to
  // hide a model that's there (irrespective of its effort suffix), so the user
  // can't end up with two entries that differ only by `@effort`.
  const takenFallbackKeys = useMemo(
    () =>
      new Set(
        (form.fallback_models ?? []).map((v) =>
          fallbackKey(v, form.cli_binary),
        ),
      ),
    [form.fallback_models, form.cli_binary],
  );

  const save = useMutation({
    mutationFn: () => {
      if (project) {
        const patch: Partial<ProjectCreate> = { ...form };
        // Don't send the empty passphrase placeholder — it would wipe the
        // existing one. Only patch it when the user actually typed something.
        if (!patch.ssh_key_passphrase) delete patch.ssh_key_passphrase;
        return projects.update(project.id, patch);
      }
      return projects.create(form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
  });

  const test = useMutation({
    mutationFn: () => projects.testSshConfig(form),
    onSuccess: (data) => setTestResult(data),
    onError: (e) =>
      setTestResult({
        ok: false,
        message: e instanceof Error ? e.message : "error",
      }),
  });

  return (
    <Modal
      title={isEdit ? `Edit ${project!.name}` : "New project"}
      description={
        isEdit
          ? "Update the project's SSH or CLI settings. Leave passphrase blank to keep the existing one."
          : "Maestro reaches the project over SSH. The CLI on the host must already be authenticated."
      }
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => test.mutate()}
            disabled={
              test.isPending ||
              !form.host ||
              !form.ssh_user ||
              !form.ssh_key_path
            }
          >
            {test.isPending ? "Testing…" : "Test SSH"}
          </Button>
          {testResult && (
            <span
              className={cn(
                "text-mono text-small",
                testResult.ok ? "text-status-success" : "text-status-error",
              )}
            >
              {testResult.ok ? "ssh ok" : `ssh fail: ${testResult.message}`}
            </span>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending || !form.name || !form.host}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map(({ key, label, placeholder, required }) => (
          <label key={key} className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-caption text-text-tertiary">
              {required && (
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: "var(--accent)" }}
                  aria-label="required"
                />
              )}
              {label}
            </span>
            <input
              type={key === "ssh_key_passphrase" ? "password" : "text"}
              className={cn(
                "h-10 px-3 bg-bg-inset rounded-md",
                "border border-transparent hover:border-border-default focus:border-border-strong",
                "text-body text-text-primary placeholder:text-text-quaternary outline-none",
                "transition-colors duration-fast ease-out",
              )}
              placeholder={placeholder}
              value={
                (form as unknown as Record<string, string | null | undefined>)[
                  key
                ] ?? ""
              }
              onChange={(e) => set(key, e.target.value as never)}
            />
          </label>
        ))}
        <div className="flex flex-col gap-1.5">
          <span className="text-caption text-text-tertiary">
            Default model
            <span className="ml-2 text-text-quaternary normal-case tracking-normal">
              dispatched as <code>--model</code> · effort is optional
            </span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            <ModelPicker
              value={form.default_model ?? ""}
              onChange={(v) => set("default_model", v || null)}
              models={allModels}
              crossProvider={false}
              defaultProvider={form.cli_binary}
              modelPlaceholder="— let CLI pick its own default"
            />
          </div>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-caption text-text-tertiary">
            Failover models
            <span className="ml-2 text-text-quaternary normal-case tracking-normal">
              tried in order when a model is rate-limited or out of usage
            </span>
          </span>
          {(form.fallback_models?.length ?? 0) > 0 && (
            <ol className="flex flex-col gap-1 mb-1">
              {(form.fallback_models ?? []).map((id, i) => {
                const label = fallbackLabel(id, fallbackOptions, form.cli_binary);
                const decoded = decodeModel(id);
                const matched = matchFallbackModel(
                  id,
                  fallbackOptions,
                  form.cli_binary,
                );
                const effortOptions = matched?.efforts ?? [];
                return (
                  <li
                    key={`${i}:${id}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-bg-inset"
                  >
                    <span className="text-caption text-text-quaternary w-4">{i + 1}.</span>
                    <span className="text-body text-text-primary flex-1 truncate">
                      {label}
                    </span>
                    <select
                      aria-label={`${label} effort`}
                      className={cn(
                        "h-7 px-1.5 bg-bg-surface rounded-md",
                        "border border-border-subtle hover:border-border-default focus:border-border-strong",
                        "text-small text-text-secondary outline-none transition-colors duration-fast ease-out",
                        "disabled:opacity-40 disabled:pointer-events-none",
                      )}
                      value={decoded.effort}
                      disabled={effortOptions.length === 0}
                      onChange={(e) =>
                        set(
                          "fallback_models",
                          setFallbackEffort(
                            form.fallback_models ?? [],
                            i,
                            e.target.value,
                          ),
                        )
                      }
                    >
                      {effortOptions.length === 0 ? (
                        <option value="">—</option>
                      ) : (
                        <>
                          <option value="">default</option>
                          {effortOptions.map((eff) => (
                            <option key={eff} value={eff}>
                              {eff}
                              {matched?.default_effort === eff ? " (default)" : ""}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    <button
                      type="button"
                      className="text-small text-text-tertiary hover:text-status-error"
                      onClick={() =>
                        set(
                          "fallback_models",
                          (form.fallback_models ?? []).filter((_, j) => j !== i),
                        )
                      }
                    >
                      remove
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          <select
            className={cn(
              "h-10 px-3 bg-bg-inset rounded-md",
              "border border-transparent hover:border-border-default focus:border-border-strong",
              "text-body text-text-primary outline-none",
              "transition-colors duration-fast ease-out",
            )}
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (
                id &&
                !takenFallbackKeys.has(fallbackKey(id, form.cli_binary))
              ) {
                set("fallback_models", [...(form.fallback_models ?? []), id]);
              }
            }}
          >
            <option value="">＋ add a failover model…</option>
            {fallbackOptions
              .map((m) => ({
                model: m,
                value: fallbackValue(m, form.cli_binary),
              }))
              .filter(
                ({ value }) =>
                  value &&
                  !takenFallbackKeys.has(fallbackKey(value, form.cli_binary)),
              )
              .map(({ model, value }) => (
                <option key={`${model.cli_binary}:${model.id}`} value={value}>
                  {fallbackLabel(value, fallbackOptions, form.cli_binary)}
                </option>
              ))}
          </select>
        </label>
      </div>

      <fieldset className="mt-5 pt-4 border-t border-border-subtle">
        <legend className="text-caption text-text-tertiary uppercase tracking-wide mb-1">
          Autonomous loops
        </legend>
        <p className="text-small text-text-tertiary mb-3">
          Opt-in. The manager classifies this project's backlog; the worker
          auto-dispatches agent-ready work behind the quota gate. The global{" "}
          <code>AUTONOMOUS_LOOPS_ENABLED</code> switch must also be on.
        </p>
        <div className="space-y-2.5">
          <Toggle
            label="Manager loop (triage)"
            hint="Classify untriaged tasks by risk + type, then route them."
            checked={!!form.manager_loop_enabled}
            onChange={(v) => set("manager_loop_enabled", v)}
          />
          <Toggle
            label="Worker loop (execute)"
            hint="Auto-dispatch agent-ready tasks, gated on quota headroom."
            checked={!!form.worker_loop_enabled}
            onChange={(v) => set("worker_loop_enabled", v)}
          />
          <Toggle
            label="Shadow mode (require human confirm)"
            hint="The worker only claims tasks a human confirmed agent-ready."
            checked={!!form.triage_requires_confirmation}
            onChange={(v) => set("triage_requires_confirmation", v)}
          />
          <Toggle
            label="Auto-merge to main"
            hint="Verified low-risk work of an allowed type lands on main with no click. Starts in shadow mode."
            checked={!!form.auto_merge}
            onChange={(v) => set("auto_merge", v)}
          />
        </div>
        {isEdit && (
          <div className="mt-3">
            <span className="text-caption text-text-tertiary">
              Task types that may auto-merge
            </span>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {Array.from(
                new Set([...TASK_TYPES, ...(form.allowed_automerge_types ?? [])]),
              ).map((t) => {
                const on = (form.allowed_automerge_types ?? []).includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      set(
                        "allowed_automerge_types",
                        on
                          ? (form.allowed_automerge_types ?? []).filter((x) => x !== t)
                          : [...(form.allowed_automerge_types ?? []), t],
                      )
                    }
                    className={cn(
                      "h-7 px-2.5 rounded-md text-small border transition-colors",
                      on
                        ? "border-border-strong bg-bg-inset text-text-primary"
                        : "border-transparent bg-bg-inset text-text-tertiary hover:border-border-default",
                    )}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-caption text-text-tertiary">
              Max concurrent auto-tasks
            </span>
            <input
              type="number"
              min={1}
              max={16}
              className={cn(
                "h-10 px-3 bg-bg-inset rounded-md border border-transparent",
                "hover:border-border-default focus:border-border-strong",
                "text-body text-text-primary outline-none transition-colors",
              )}
              value={form.max_concurrent_auto ?? 1}
              onChange={(e) =>
                set("max_concurrent_auto", Math.max(1, Number(e.target.value) || 1))
              }
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-caption text-text-tertiary">
              Quota pause threshold
              <span className="ml-2 text-text-quaternary normal-case tracking-normal">
                pause new dispatch at this 5h/7d utilization
              </span>
            </span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.05}
              className={cn(
                "h-10 px-3 bg-bg-inset rounded-md border border-transparent",
                "hover:border-border-default focus:border-border-strong",
                "text-body text-text-primary outline-none transition-colors",
              )}
              value={form.quota_pause_threshold ?? 0.9}
              onChange={(e) =>
                set(
                  "quota_pause_threshold",
                  Math.min(1, Math.max(0, Number(e.target.value) || 0)),
                )
              }
            />
          </label>
        </div>
        <RiskPolicyEditor
          value={form.risk_autonomy_policy}
          onChange={(v) => set("risk_autonomy_policy", v)}
        />
      </fieldset>

      {save.isError && (
        <div className="mt-3 text-small text-status-error mono">
          {(save.error as Error).message}
        </div>
      )}
    </Modal>
  );
}

// ----- Risk → autonomy policy editor --------------------------------------
//
// Per-risk override of the built-in routing + autonomy mode. "Default" on a
// field means "omit it — use the built-in policy". An override can only tighten,
// never loosen: the backend ignores a route that would promote a needs_human
// default to agent-ready. Nothing dispatched by Maestro pushes.

type RiskEntry = { route?: string; autonomy?: string };
type RiskPolicy = Record<string, unknown>;

const RISK_ROWS: Array<{ key: "low" | "medium" | "high"; builtin: string }> = [
  { key: "low", builtin: "agent-ready · agent" },
  { key: "medium", builtin: "agent-ready · agent" },
  { key: "high", builtin: "needs human · never auto-runs" },
];

const ROUTE_OPTIONS = [
  { value: "__default__", label: "Default" },
  { value: "agent_ready", label: "agent-ready" },
  { value: "needs_human", label: "needs human" },
];
const AUTONOMY_OPTIONS = [
  { value: "__default__", label: "Default" },
  { value: "agent", label: "agent (write + commit)" },
  { value: "read_only", label: "read-only" },
];

function readEntry(policy: RiskPolicy | undefined, risk: string): RiskEntry {
  const raw = policy?.[risk];
  if (typeof raw === "string") return { autonomy: raw }; // shorthand form
  if (raw && typeof raw === "object") return raw as RiskEntry;
  return {};
}

function writeField(
  policy: RiskPolicy | undefined,
  risk: string,
  field: keyof RiskEntry,
  value: string,
): RiskPolicy {
  const next: RiskPolicy = { ...(policy ?? {}) };
  const entry: RiskEntry = { ...readEntry(policy, risk) };
  if (value === "__default__") delete entry[field];
  else entry[field] = value;
  if (!entry.route && !entry.autonomy) delete next[risk];
  else next[risk] = entry;
  return next;
}

function RiskPolicyEditor({
  value,
  onChange,
}: {
  value: RiskPolicy | undefined;
  onChange: (v: RiskPolicy) => void;
}) {
  const selectCls = cn(
    "h-9 px-2 bg-bg-inset rounded-md border border-transparent",
    "hover:border-border-default focus:border-border-strong",
    "text-small text-text-primary outline-none transition-colors",
  );
  return (
    <div className="mt-4">
      <div className="text-caption text-text-tertiary uppercase tracking-wide mb-1">
        Risk → autonomy policy
      </div>
      <p className="text-small text-text-tertiary mb-2.5">
        Override how each risk is routed and how much autonomy it runs at.
        Leave on <span className="text-text-secondary">Default</span> to use the
        built-in policy. Overrides can only tighten — autonomous runs never push
        unless the global <code>AUTONOMOUS_ALLOW_PUSH</code> is on.
      </p>
      <div className="space-y-2">
        {RISK_ROWS.map((row) => {
          const entry = readEntry(value, row.key);
          return (
            <div
              key={row.key}
              className="grid grid-cols-[64px_1fr_1fr] items-center gap-2"
            >
              <span className="text-small text-text-secondary capitalize">
                {row.key}
              </span>
              <select
                aria-label={`${row.key} route`}
                className={selectCls}
                value={entry.route ?? "__default__"}
                onChange={(e) =>
                  onChange(writeField(value, row.key, "route", e.target.value))
                }
              >
                {ROUTE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                aria-label={`${row.key} autonomy`}
                className={selectCls}
                value={entry.autonomy ?? "__default__"}
                onChange={(e) =>
                  onChange(
                    writeField(value, row.key, "autonomy", e.target.value),
                  )
                }
              >
                {AUTONOMY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-[color:var(--accent)] cursor-pointer"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex flex-col">
        <span className="text-body text-text-primary leading-tight">{label}</span>
        <span className="text-small text-text-tertiary leading-tight">{hint}</span>
      </span>
    </label>
  );
}

function fallbackValue(model: ModelChoice, primaryCli: string | undefined): string {
  if (model.cli_binary === primaryCli) return model.id;
  return `${model.cli_binary}:${model.id}`;
}

function resolvedCli(
  decoded: { provider: string },
  primaryCli: string | undefined,
): string {
  return decoded.provider || primaryCli || "claude";
}

function fallbackKey(value: string, primaryCli: string | undefined): string {
  // The (cli, model) tuple, ignoring any `@effort` suffix. Used for dedupe and
  // catalog matching so the same model at two efforts can't both sit in the
  // chain (the second one would never get tried anyway — failover walks past).
  const decoded = decodeModel(value);
  return `${resolvedCli(decoded, primaryCli)}:${decoded.model}`;
}

function matchFallbackModel(
  value: string,
  options: ModelChoice[],
  primaryCli: string | undefined,
): ModelChoice | undefined {
  const decoded = decodeModel(value);
  const cli = resolvedCli(decoded, primaryCli);
  return options.find((m) => m.cli_binary === cli && m.id === decoded.model);
}

function fallbackLabel(
  value: string,
  options: ModelChoice[],
  primaryCli: string | undefined,
): string {
  const decoded = decodeModel(value);
  const cli = resolvedCli(decoded, primaryCli);
  const match = options.find((m) => m.cli_binary === cli && m.id === decoded.model);
  const label = match?.label ?? (decoded.model || "CLI default");
  return cli === primaryCli ? label : `${label} · ${cli}`;
}

function setFallbackEffort(
  list: string[],
  index: number,
  effort: string,
): string[] {
  const next = list.slice();
  const current = next[index] ?? "";
  const decoded = decodeModel(current);
  next[index] = encodeModel({
    provider: decoded.provider,
    model: decoded.model,
    effort,
  });
  return next;
}
